import { gql } from '@apollo/client';

export const GET_CHARTABLE_FIELDS = gql`
  query GetChartableFields($templateId: ID!) {
    chartableFields(templateId: $templateId) {
      ref
      label
      questionType
      presentInVersionIds
      isAnonymousSource
      isScored
      hasInconsistentScale
      suggestedScale {
        presetId
        name
        min
        max
        optionScores {
          optionId
          label
          score
        }
      }
      options {
        label
        score
        notApplicable
        presentInVersionIds
      }
      wordings {
        versionId
        text
      }
    }
  }
`;

/**
 * Batched on purpose: a dashboard sends every tile in one call, and specs
 * sharing a survey and date window are read once between them on the server.
 */
export const RUN_CHART_QUERIES = gql`
  query RunChartQueries($specs: [JSON!]!) {
    runChartQueries(specs: $specs)
  }
`;

/**
 * Note what this does not ask for: there is no p-value on a highlight, because
 * the schema does not offer one. The correction that selected these items ran
 * on the server; "significant" is not a word this panel gets to use.
 */
export const GET_SURVEY_HIGHLIGHTS = gql`
  query GetSurveyHighlights(
    $templateId: ID!
    $dateRange: JSON
    $segment: JSON
    $measure: String
  ) {
    surveyHighlights(
      templateId: $templateId
      dateRange: $dateRange
      segment: $segment
      measure: $measure
    ) {
      highlights {
        questionKey
        questionLabel
        segmentKey
        segmentLabel
        value
        interval {
          low
          high
        }
        n
        restValue
        restN
        difference
        probabilityOfSuperiority
        isCensus
        responseRate
        otherSegmentsFlagged
      }
      disclosure {
        comparisons
        questionsScanned
        segmentsScanned
        q
        expectedFalse
        belowEffectFloor
        refusedForSize
      }
      segmentLevel
      levels {
        level
        label
        groups
        comparableGroups
      }
      respondentsInScope
      scoredQuestions
      unscoredQuestions
      segmentLabel
      measure
      generatedAt
    }
  }
`;

/**
 * Deliberately descriptive, and deliberately not a model. See the note on the
 * RankedItem type: there is no outcome variable in a climate survey to drive,
 * so "where are we weakest" is answered by ranking the items themselves.
 */
export const GET_SURVEY_ITEM_RANKING = gql`
  query GetSurveyItemRanking(
    $templateId: ID!
    $dateRange: JSON
    $measure: String
  ) {
    surveyItemRanking(
      templateId: $templateId
      dateRange: $dateRange
      measure: $measure
    ) {
      items {
        questionKey
        questionLabel
        value
        interval {
          low
          high
        }
        n
        treatment
        notApplicable
      }
      respondentsInScope
      scoredQuestions
      unscoredQuestions
      measure
      generatedAt
    }
  }
`;

const SAVED_CHART_FIELDS = `
  id
  name
  description
  templateId
  spec
  specVersion
  visibility
  createdByUserId
  createdBy { id name isYou }
  createdAt
  updatedAt
`;

export const GET_SAVED_CHARTS = gql`
  query GetSavedCharts($templateId: ID) {
    savedCharts(templateId: $templateId) { ${SAVED_CHART_FIELDS} }
  }
`;

export const CREATE_SAVED_CHART = gql`
  mutation CreateSavedChart($input: SavedChartInput!) {
    createSavedChart(input: $input) { ${SAVED_CHART_FIELDS} }
  }
`;

export const UPDATE_SAVED_CHART = gql`
  mutation UpdateSavedChart($id: ID!, $input: SavedChartInput!) {
    updateSavedChart(id: $id, input: $input) { ${SAVED_CHART_FIELDS} }
  }
`;

export const DELETE_SAVED_CHART = gql`
  mutation DeleteSavedChart($id: ID!) {
    deleteSavedChart(id: $id) {
      id
    }
  }
`;

const DASHBOARD_FIELDS = `
  id
  name
  ownerUserId
  collaboratorUserIds
  isOwner
  createdBy { id name isYou }
  dateRangeOverride
  breakdownOverride
  createdAt
  updatedAt
  tiles {
    id
    chartId
    size
    height
    titleOverride
    dateRangeOverride
  }
`;

export const GET_DASHBOARDS = gql`
  query GetDashboards {
    dashboards { ${DASHBOARD_FIELDS} }
  }
`;

export const GET_COMPANY_MEMBERS = gql`
  query GetCompanyMembers {
    companyMembers {
      id
      firstName
      lastName
      email
    }
  }
`;

export const GET_DEFAULT_DASHBOARD = gql`
  query GetDefaultDashboard {
    defaultDashboard { ${DASHBOARD_FIELDS} }
  }
`;

export const PIN_CHART = gql`
  mutation PinChart($input: SavedChartInput!, $dashboardId: ID) {
    pinChart(input: $input, dashboardId: $dashboardId) { ${DASHBOARD_FIELDS} }
  }
`;

export const UNPIN_CHART = gql`
  mutation UnpinChart($chartId: ID!, $dashboardId: ID) {
    unpinChart(chartId: $chartId, dashboardId: $dashboardId) {
      ${DASHBOARD_FIELDS}
    }
  }
`;

export const CREATE_DASHBOARD = gql`
  mutation CreateDashboard($name: String!) {
    createDashboard(name: $name) { ${DASHBOARD_FIELDS} }
  }
`;

export const RENAME_DASHBOARD = gql`
  mutation RenameDashboard($id: ID!, $name: String!) {
    renameDashboard(id: $id, name: $name) { ${DASHBOARD_FIELDS} }
  }
`;

export const DELETE_DASHBOARD = gql`
  mutation DeleteDashboard($id: ID!) {
    deleteDashboard(id: $id)
  }
`;

export const LEAVE_DASHBOARD = gql`
  mutation LeaveDashboard($id: ID!) {
    leaveDashboard(id: $id)
  }
`;

export const SAVE_DASHBOARD = gql`
  mutation SaveDashboard(
    $id: ID!
    $tiles: [DashboardTileInput!]!
    $expectedUpdatedAt: String
  ) {
    saveDashboard(id: $id, tiles: $tiles, expectedUpdatedAt: $expectedUpdatedAt) {
      ${DASHBOARD_FIELDS}
    }
  }
`;

export const SET_DASHBOARD_OVERRIDES = gql`
  mutation SetDashboardOverrides(
    $id: ID!
    $dateRangeOverride: JSON
    $breakdownOverride: String
  ) {
    setDashboardOverrides(
      id: $id
      dateRangeOverride: $dateRangeOverride
      breakdownOverride: $breakdownOverride
    ) { ${DASHBOARD_FIELDS} }
  }
`;

export const SET_DASHBOARD_COLLABORATORS = gql`
  mutation SetDashboardCollaborators($id: ID!, $userIds: [ID!]!) {
    setDashboardCollaborators(id: $id, userIds: $userIds) { ${DASHBOARD_FIELDS} }
  }
`;

export const COPY_DASHBOARD_TO = gql`
  mutation CopyDashboardTo($id: ID!, $userId: ID!) {
    copyDashboardTo(id: $id, userId: $userId) { ${DASHBOARD_FIELDS} }
  }
`;

export const ANNOTATE_QUESTION_SCALE = gql`
  mutation AnnotateQuestionScale(
    $templateId: ID!
    $questionId: ID!
    $scale: ScaleAnnotationInput
    $options: [OptionAnnotationInput!]!
  ) {
    annotateQuestionScale(
      templateId: $templateId
      questionId: $questionId
      scale: $scale
      options: $options
    ) {
      questionId
      versionsAnnotated
      versionsSkipped
      results {
        versionId
        outcome
        changedOptionIds
        unknownOptionIds
      }
    }
  }
`;

/**
 * Where the reader left the results page.
 *
 * dateRange is global and follows them between surveys; byTemplate is keyed by
 * survey, and within that by question, so it only means anything in the survey
 * it belongs to.
 */
export const GET_MY_BUILDER_PREFERENCE = gql`
  query GetMyBuilderPreference {
    myBuilderPreference {
      lastTemplateId
      dateRange
      byTemplate
      updatedAt
    }
  }
`;

export const SAVE_BUILDER_PREFERENCE = gql`
  mutation SaveBuilderPreference($input: BuilderPreferenceInput!) {
    saveBuilderPreference(input: $input) {
      lastTemplateId
      dateRange
      byTemplate
      updatedAt
    }
  }
`;
